import { createContext, ReactNode, useState, useEffect, useContext, useRef, useMemo } from 'react';
import { captureMessage } from '@sentry/nextjs';
import ConfigRepo, { IConfig } from '../../repository/config';
import { getUrlParam, updateQueryStringParameter } from '../../utility/k_url';
import Routes from '../../service/routes';
import { useQlubRouter } from '../../hook/router';
import { supportedLanguages } from '../../const';
import SplashLegacy from '../../component/SplashLegacy';
import { useAuth } from '../auth';
import { qlubSessionStorage } from '../../service/storage/sessionStorage';
import { appVersion } from '../../repository/axios/constants';
import VendorAPIManager from '../../repository/vendor';
import { getDefaultLanguage } from '../../utility/k_lang';
import { applyThemeToURL } from '../../utility/theme';

const splashWarningTimeout = 10000;

const noSplashPages = ['/qr/[cc]/[slug]/[id]/[f1]/[f2]/[hash]/invoice/iframe'];

const hasHideLogoInUrl = () => {
    if (!process.browser || !window) {
        return false;
    }

    try {
        const hideLogo = new URLSearchParams(window.location.search).get('hideLogo') === 'true';
        if (hideLogo) {
            document?.querySelector('html')?.setAttribute('hideLogo', 'true');
        }
        return hideLogo;
    } catch {
        return false;
    }
};

export const themeOptions = {
    inUrl: false,
    hideLogo: hasHideLogoInUrl(),
};

export const shouldHideLogo = (hideLogo: string, defaultVal?: boolean) => {
    if (themeOptions.hideLogo) {
        return true;
    }

    if (hideLogo === 'true') {
        themeOptions.hideLogo = true;
        return true;
    }

    return defaultVal || false;
};

export const checkVendorCondition = (options?: string[], cc?: string, slug?: string) => {
    if (!options || !cc || !slug) {
        return false;
    }
    return options?.map((o) => o?.split('|')?.[0] === cc && o?.split('|')?.[1]).includes(slug);
};

export enum ThemeModeEnum {
    Normal = 'normal',
    Black = 'black',
    Blue = 'blue',
    Green = 'green',
    Darkred = 'darkred',
    Pink = 'pink',
    Orange = 'orange',
    DarkGold = 'darkgold',
    Rose = 'rose',
}

interface ISplashMetrics {
    slug?: string;
    cc?: string;
    tableId?: string;
    configStatus?: string;
    authStatus?: string;
}

export interface IConfigContext {
    config: IConfig | null;
}

const keyAuthError = 'qlub.auth.error';

const ConfigContext = createContext<IConfigContext>({
    config: null,
});

interface IProps {
    children?: ReactNode;
}

export function ConfigContextProvider({ children }: IProps) {
    const { asPath, isReady, url, push, lang, pathname, theme, query } = useQlubRouter();
    const [init, setInit] = useState(false);
    const [config, setConfig] = useState<IConfig | null>(null);
    const [showSplash, setShowSplash] = useState(false);
    const [hasAuthError, setHasAuthError] = useState(qlubSessionStorage.get(keyAuthError) === 'true');
    const timeoutRef = useRef<any>(null);
    const reloadTimeoutRef = useRef<any>(null);
    const metricsRef = useRef<ISplashMetrics>({});
    const { login, isLogin } = useAuth();

    useEffect(() => {
        metricsRef.current = {
            ...metricsRef.current,
            cc: url.cc,
            slug: url.slug,
            tableId: url.slug,
        };
    }, [url]);

    useEffect(() => {
        if (!isReady || !theme) {
            return;
        }

        if (!themeOptions.inUrl) {
            applyThemeToURL(theme);
        }

        themeOptions.inUrl = true;
    }, [isReady]);

    const hideLogo = useMemo(() => {
        return shouldHideLogo(query.hideLogo);
    }, [query]);

    useEffect(() => {
        if (appVersion) {
            // @ts-ignore
            window?.clarity?.('set', 'app_version', appVersion);
        }

        if (isLogin) {
            return () => {
                //
            };
        }

        login({ guest: true })
            .then(() => {
                metricsRef.current = {
                    ...metricsRef.current,
                    authStatus: 'success',
                };
            })
            .catch((err) => {
                metricsRef.current = {
                    ...metricsRef.current,
                    authStatus: err?.message || 'fail',
                };
                setHasAuthError(true);
                qlubSessionStorage.set(keyAuthError, 'true');
            });

        timeoutRef.current = setTimeout(() => {
            qlubSessionStorage.set(keyAuthError, 'true');
            captureMessage(`splash screen took longer than ${splashWarningTimeout}ms`, {
                tags: {
                    ...metricsRef.current,
                },
            });
            // @ts-ignore
            window?.clarity?.('set', 'stuck-in-splash', 'true');
        }, splashWarningTimeout);

        reloadTimeoutRef.current = setTimeout(() => {
            window.location.reload();
        }, splashWarningTimeout * 1.5);

        return () => {
            clearTimeout(timeoutRef.current);
            clearTimeout(reloadTimeoutRef.current);
        };
    }, []);

    const applyDarkTheme = (res: IConfig, path: string) => {
        if (themeOptions.inUrl) {
            return path;
        }

        const params = Routes.getInstance().getVendorParams(path);
        if (!params || !checkVendorCondition(res?.customerAppBlackMode, params.cc, params.slug)) {
            return path;
        }

        applyThemeToURL(ThemeModeEnum.Black);
        return updateQueryStringParameter(path, 'theme', ThemeModeEnum.Black);
    };

    useEffect(() => {
        if (Routes.getInstance().isNonCountryBasedUrl(asPath)) {
            setShowSplash(false);
            return;
        }

        setShowSplash(true);

        if (init || !url.cc || !url.cc) {
            return;
        }

        ConfigRepo.getInstance()
            ?.getConfig(url.cc)
            ?.then((res) => {
                metricsRef.current = {
                    ...metricsRef.current,
                    configStatus: 'success',
                };
                setConfig(res);
                let qUrl = asPath;
                let langToCheck = getDefaultLanguage(getUrlParam(asPath, 'lang') || res.defaultLang || lang);
                if (supportedLanguages.indexOf(langToCheck || '') === -1) {
                    langToCheck = res.defaultLang;
                }
                if (langToCheck) {
                    qUrl = updateQueryStringParameter(asPath, 'lang', langToCheck);
                }
                qUrl = applyDarkTheme(res, qUrl);
                if (qUrl !== asPath) {
                    push(qUrl).then(() => {
                        setInit(true);
                    });
                } else {
                    setInit(true);
                }
            })
            .catch((err) => {
                metricsRef.current = {
                    ...metricsRef.current,
                    configStatus: err?.message || 'fail',
                };
                setInit(true);
            });
    }, [isReady, url.cc, asPath]);

    useEffect(() => {
        if (!isReady || !url.cc || !url.slug) {
            return;
        }

        if (!Routes.getInstance().isWithBackgroundUrl(asPath)) {
            const body = document.querySelector('body');
            if (body) {
                body.style.setProperty('background-image', '');
            }
        }
    }, [isReady, asPath, url.cc, url.slug]);

    useEffect(() => {
        if (!isReady || !url.cc || !url.slug || !url.f1 || !url.f2) {
            return;
        }

        VendorAPIManager.getInstance().getRestaurantInfo(url);
    }, [isReady, url.cc, url.slug, url.f1, url.f2]);

    const splash = (showSplash && !init && !noSplashPages.includes(pathname)) || (!isLogin && !hasAuthError);

    useEffect(() => {
        if (splash) {
            return;
        }
        clearTimeout(timeoutRef.current);
        clearTimeout(reloadTimeoutRef.current);
    }, [splash]);

    if (splash) {
        return <SplashLegacy hideIcon={hideLogo} />;
    }

    // eslint-disable-next-line react/jsx-no-constructed-context-values
    return <ConfigContext.Provider value={{ config }}> {children} </ConfigContext.Provider>;
}

export const useConfigContext = () => useContext(ConfigContext);
